// @ts-check
// Formulários do caminho (contrato de bots, seções 4.3 a 4.7): condição com até 10 regras (variável, campo do CRM,
// departamento aberto, número, situação da família, nível de identificação e horário), divisão por porcentagem (2 a 4
// ramos que somam 100), variável, "Ir para" (o destino no canvas), subbot (outro bot, com as variáveis passadas) e
// encerrar.
import { html } from 'htm/preact';
import { Button } from '../../../ui/button.js';
import { Checkbox } from '../../../ui/feedback.js';
import { Icon } from '../../../ui/icon.js';
import { SegmentedControl } from '../../../ui/segmented-control.js';
import { gotoTargetChoices } from '../../../lib/bot-editor-state.js';
import { SPLIT_PERCENT_KEYS } from '../../../lib/bot-tree.js';
import { BOT_CONDITION_RULE_KINDS, BOT_RECORD_TARGETS } from '../../../../../shared/bots/bot-definition.js';
import { MAXIMUM_BOT_CONDITION_RULES, MAXIMUM_BOT_SET_VARIABLE_VALUE_LENGTH } from '../../../../../shared/bots/bot-limits.js';
import { FlowField, FlowNotice } from '../../flows/flow-ui.js';
import { BotSelectField, BotTextField, useFieldId } from '../bot-form-fields.js';
import { targetObject } from '../bot-lookups.js';
import { botFieldProblemText, botProblemText, describeBotNode } from '../bot-ui.js';
import { translateMetadataLabel, t } from '../../../i18n/index.js';

/** @typedef {import('../bot-node-panel.js').BotNodeFormProps} BotNodeFormProps */

const VARIABLE_OPERATORS = Object.freeze(['equals', 'notEquals', 'contains', 'isEmpty', 'isNotEmpty', 'greaterThan', 'lessThan']);
const FIELD_OPERATORS = Object.freeze(['equals', 'notEquals', 'contains', 'isEmpty', 'isNotEmpty']);
const VALUELESS_OPERATORS = new Set(['isEmpty', 'isNotEmpty']);
const WEEKDAYS = Object.freeze(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);

/**
 * Regra nova do tipo, com os campos que ele pede.
 * @param {string} kind @returns {Record<string, any>}
 */
export function newConditionRule(kind) {
  switch (kind) {
    case 'VARIABLE': return { kind, name: '', operator: 'equals', value: '' };
    case 'FIELD': return { kind, target: 'CARD', fieldName: '', operator: 'equals', value: '' };
    case 'TEAM_OPEN': return { kind, teamId: '', operator: 'isOpen' };
    case 'CHANNEL': return { kind, operator: 'in', channelIds: [] };
    case 'FAMILY_STATUS': return { kind, operator: 'equals', value: 'CURRENT' };
    case 'IDENTITY_LEVEL': return { kind, operator: 'atLeast', value: 'PHONE_MATCH' };
    default: return { kind: 'TIME_OF_DAY', operator: 'between', value: { from: '08:00', to: '18:00', weekdays: ['MON', 'TUE', 'WED', 'THU', 'FRI'] } };
  }
}

/** @param {BotNodeFormProps} props */
export function ConditionForm({ node, problems, variables, lookupData, onConfig }) {
  const config = node.config ?? {};
  const rules = Array.isArray(config.rules) ? config.rules : [];
  /** @param {number} position @param {Record<string, any>} patch */
  const updateRule = (position, patch) => onConfig({ rules: rules.map((rule, index) => (index === position ? { ...rule, ...patch } : rule)) });
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-condition-form">
    <div class="flow-field">
      <span class="flow-field-label">${t('bots.forms.condition.logic')}</span>
      <${SegmentedControl} label=${t('bots.forms.condition.logic')} value=${config.logic === 'OR' ? 'OR' : 'AND'}
        options=${[{ value: 'AND', label: t('bots.forms.condition.all') }, { value: 'OR', label: t('bots.forms.condition.any') }]}
        onChange=${(/** @type {string} */ logic) => onConfig({ logic })} />
    </div>
    <ol class="bot-rules">
      ${rules.map((rule, position) => {
        const error = botFieldProblemText(problems, `rules.${position}`);
        return html`<li key=${position} class="bot-rule" data-invalid=${error ? 'true' : undefined}>
          <div class="bot-rule-head">
            <select class="text-input" value=${rule.kind} aria-label=${t('bots.forms.condition.ruleKind', { number: position + 1 })}
              onChange=${(/** @type {Event} */ event) => onConfig({ rules: rules.map((item, index) => (index === position ? newConditionRule(/** @type {HTMLSelectElement} */ (event.currentTarget).value) : item)) })}>
              ${BOT_CONDITION_RULE_KINDS.map((kind) => html`<option key=${kind} value=${kind}>${t(`bots.conditionKinds.${kind}`)}</option>`)}
            </select>
            <button type="button" class="bot-option-remove" aria-label=${t('bots.forms.condition.removeRule', { number: position + 1 })}
              onClick=${() => onConfig({ rules: rules.filter((_, index) => index !== position) })}><${Icon} name="IconTrash" size=${14} /></button>
          </div>
          <${ConditionRuleFields} rule=${rule} variables=${variables} lookupData=${lookupData} onChange=${(/** @type {Record<string, any>} */ patch) => updateRule(position, patch)} />
          ${error ? html`<span class="flow-field-error" role="alert">${error}</span>` : null}
        </li>`;
      })}
    </ol>
    ${rules.length === 0 ? html`<p class="flow-field-error" role="alert">${botFieldProblemText(problems, 'rules') ?? ''}</p>` : null}
    ${rules.length < MAXIMUM_BOT_CONDITION_RULES
      ? html`<${Button} title=${t('bots.forms.condition.addRule')} icon="IconPlus" variant="secondary" size="small" data-testid="bot-add-rule"
          onClick=${() => onConfig({ rules: [...rules, newConditionRule('VARIABLE')] })} />`
      : html`<p class="flow-field-hint">${t('bots.forms.condition.limit', { maximum: MAXIMUM_BOT_CONDITION_RULES })}</p>`}
    <p class="flow-field-hint">${t('bots.forms.condition.branchesHint')}</p>
  </div>`;
}

/**
 * Campos de uma regra da condição, pelo tipo.
 * @param {{ rule: Record<string, any>, variables: string[], lookupData: BotNodeFormProps['lookupData'], onChange: (patch: Record<string, any>) => void }} props
 */
function ConditionRuleFields({ rule, variables, lookupData, onChange }) {
  /** @param {string[]} operators */
  const operatorSelect = (operators) => html`<select class="text-input" value=${rule.operator} aria-label=${t('bots.forms.condition.operator')}
    onChange=${(/** @type {Event} */ event) => onChange({ operator: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
    ${operators.map((operator) => html`<option key=${operator} value=${operator}>${t(`bots.conditionOperators.${operator}`)}</option>`)}
  </select>`;
  const valueInput = html`<input class="text-input" value=${rule.value ?? ''} aria-label=${t('bots.forms.condition.value')} placeholder=${t('bots.forms.condition.value')}
    onInput=${(/** @type {InputEvent} */ event) => onChange({ value: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />`;
  switch (rule.kind) {
    case 'VARIABLE':
      return html`<div class="bot-rule-fields">
        <select class="text-input" value=${rule.name ?? ''} aria-label=${t('bots.forms.condition.variable')}
          onChange=${(/** @type {Event} */ event) => onChange({ name: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
          <option value="">${t('bots.forms.condition.chooseVariable')}</option>
          ${[...new Set([...variables, ...(rule.name ? [rule.name] : [])])].map((name) => html`<option key=${name} value=${name}>${`bot.${name}`}</option>`)}
        </select>
        ${operatorSelect([...VARIABLE_OPERATORS])}
        ${VALUELESS_OPERATORS.has(rule.operator) ? null : valueInput}
      </div>`;
    case 'FIELD': {
      const object = targetObject(rule.target ?? 'CARD');
      const fields = (object?.fields ?? []).filter((/** @type {any} */ field) => field.isActive !== false && !field.isSystem);
      return html`<div class="bot-rule-fields">
        <select class="text-input" value=${rule.target ?? 'CARD'} aria-label=${t('bots.forms.saveTo.target')}
          onChange=${(/** @type {Event} */ event) => onChange({ target: /** @type {HTMLSelectElement} */ (event.currentTarget).value, fieldName: '' })}>
          ${BOT_RECORD_TARGETS.map((target) => html`<option key=${target} value=${target}>${t(`bots.recordTargets.${target}`)}</option>`)}
        </select>
        <select class="text-input" value=${rule.fieldName ?? ''} aria-label=${t('bots.forms.saveTo.field')}
          onChange=${(/** @type {Event} */ event) => onChange({ fieldName: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
          <option value="">${t('bots.forms.choose')}</option>
          ${fields.map((/** @type {any} */ field) => html`<option key=${field.name} value=${field.name}>${translateMetadataLabel(field.label)}</option>`)}
        </select>
        ${operatorSelect([...FIELD_OPERATORS])}
        ${VALUELESS_OPERATORS.has(rule.operator) ? null : valueInput}
      </div>`;
    }
    case 'TEAM_OPEN':
      return html`<div class="bot-rule-fields">
        <select class="text-input" value=${rule.teamId ?? ''} aria-label=${t('bots.forms.team')}
          onChange=${(/** @type {Event} */ event) => onChange({ teamId: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
          <option value="">${t('bots.forms.chooseTeam')}</option>
          ${(lookupData.teams ?? []).map((team) => html`<option key=${team.id} value=${team.id}>${team.name}</option>`)}
        </select>
        ${operatorSelect(['isOpen', 'isClosed'])}
      </div>`;
    case 'CHANNEL':
      return html`<div class="bot-rule-fields bot-rule-checks" role="group" aria-label=${t('bots.forms.condition.channels')}>
        ${(lookupData.channels ?? []).map((channel) => html`<label key=${channel.id} class="flow-checkbox-row">
          <${Checkbox} checked=${(rule.channelIds ?? []).includes(channel.id)} ariaLabel=${channel.label}
            onChange=${(/** @type {boolean} */ checked) => onChange({ channelIds: checked ? [...(rule.channelIds ?? []), channel.id] : (rule.channelIds ?? []).filter((/** @type {string} */ id) => id !== channel.id) })} />
          <span>${channel.label}</span>
        </label>`)}
      </div>`;
    case 'FAMILY_STATUS':
      return html`<div class="bot-rule-fields">
        <select class="text-input" value=${rule.value} aria-label=${t('bots.conditionKinds.FAMILY_STATUS')}
          onChange=${(/** @type {Event} */ event) => onChange({ value: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
          ${['CURRENT', 'LEAD', 'UNKNOWN'].map((value) => html`<option key=${value} value=${value}>${t(`bots.familyStatuses.${value}`)}</option>`)}
        </select>
      </div>`;
    case 'IDENTITY_LEVEL':
      return html`<div class="bot-rule-fields">
        <span class="bot-rule-text">${t('bots.conditionOperators.atLeast')}</span>
        <select class="text-input" value=${rule.value} aria-label=${t('bots.conditionKinds.IDENTITY_LEVEL')}
          onChange=${(/** @type {Event} */ event) => onChange({ value: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
          ${['PHONE_MATCH', 'WEAK_DATA', 'STRONG_CODE'].map((value) => html`<option key=${value} value=${value}>${t(`bots.identityLevels.${value}`)}</option>`)}
        </select>
      </div>`;
    default: {
      const value = rule.value ?? {};
      const weekdays = Array.isArray(value.weekdays) ? value.weekdays : [];
      return html`<div class="bot-rule-fields bot-rule-time">
        <span class="bot-rule-text">${t('bots.forms.condition.between')}</span>
        <input type="time" class="text-input" value=${value.from ?? ''} aria-label=${t('bots.forms.condition.from')}
          onInput=${(/** @type {InputEvent} */ event) => onChange({ value: { ...value, from: /** @type {HTMLInputElement} */ (event.currentTarget).value } })} />
        <input type="time" class="text-input" value=${value.to ?? ''} aria-label=${t('bots.forms.condition.to')}
          onInput=${(/** @type {InputEvent} */ event) => onChange({ value: { ...value, to: /** @type {HTMLInputElement} */ (event.currentTarget).value } })} />
        <div class="bot-weekdays" role="group" aria-label=${t('bots.forms.weekdays')}>
          ${WEEKDAYS.map((day) => html`<label key=${day} class="bot-weekday" data-checked=${weekdays.includes(day) ? 'true' : undefined}>
            <input type="checkbox" checked=${weekdays.includes(day)}
              onChange=${(/** @type {Event} */ event) => onChange({ value: { ...value, weekdays: /** @type {HTMLInputElement} */ (event.currentTarget).checked ? [...weekdays, day] : weekdays.filter((/** @type {string} */ item) => item !== day) } })} />
            <span>${t(`bots.weekdays.${day}`)}</span>
          </label>`)}
        </div>
        <p class="flow-field-hint">${t('bots.forms.condition.timeZone')}</p>
      </div>`;
    }
  }
}

/** @param {BotNodeFormProps} props */
export function SplitPercentForm({ node, problems, onConfig }) {
  const shares = node.config?.shares ?? {};
  const keys = SPLIT_PERCENT_KEYS.filter((key) => typeof shares[key] === 'number');
  const total = keys.reduce((sum, key) => sum + Number(shares[key] ?? 0), 0);
  /** @param {Record<string, number>} next */
  const set = (next) => onConfig({ shares: next });
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-split-form">
    <ul class="bot-split-list">
      ${keys.map((key) => html`<li key=${key} class="bot-split-row">
        <span class="bot-split-letter">${key.toUpperCase()}</span>
        <input type="number" class="text-input" min="0" max="100" value=${shares[key]} aria-label=${t('bots.forms.split.share', { letter: key.toUpperCase() })}
          onInput=${(/** @type {InputEvent} */ event) => set({ ...shares, [key]: Math.trunc(Number(/** @type {HTMLInputElement} */ (event.currentTarget).value || 0)) })} />
        <span aria-hidden="true">%</span>
        ${keys.length > 2
          ? html`<button type="button" class="bot-option-remove" aria-label=${t('bots.forms.split.remove', { letter: key.toUpperCase() })}
              onClick=${() => set(Object.fromEntries(Object.entries(shares).filter(([candidate]) => candidate !== key)))}><${Icon} name="IconTrash" size=${14} /></button>`
          : null}
      </li>`)}
    </ul>
    <p class="bot-counter" data-over=${total !== 100 ? 'true' : undefined}>${t('bots.forms.split.total', { total })}</p>
    ${botFieldProblemText(problems, 'shares') ? html`<p class="flow-field-error" role="alert">${botFieldProblemText(problems, 'shares')}</p>` : null}
    ${keys.length < SPLIT_PERCENT_KEYS.length
      ? html`<${Button} title=${t('bots.forms.split.add')} icon="IconPlus" variant="secondary" size="small"
          onClick=${() => set({ ...shares, [/** @type {string} */ (SPLIT_PERCENT_KEYS.find((key) => !keys.includes(key)))]: 0 })} />`
      : null}
    <p class="flow-field-hint">${t('bots.forms.split.hint')}</p>
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function SetVariableForm({ node, problems, variables, onConfig }) {
  const config = node.config ?? {};
  const id = useFieldId('bot-variable-name');
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-set-variable-form">
    <${FlowField} label=${t('bots.forms.saveTo.variable')} htmlFor=${id} error=${botFieldProblemText(problems, 'name')} hint=${t('bots.forms.saveTo.variableHint')}>
      <div class="bot-variable-name">
        <span class="bot-variable-prefix" aria-hidden="true">bot.</span>
        <input id=${id} class="text-input" value=${config.name ?? ''} onInput=${(/** @type {InputEvent} */ event) => onConfig({ name: /** @type {HTMLInputElement} */ (event.currentTarget).value.replace(/[^a-zA-Z0-9]/g, '') })} />
      </div>
    <//>
    <${BotTextField} label=${t('bots.forms.setVariable.value')} value=${config.value} maximum=${MAXIMUM_BOT_SET_VARIABLE_VALUE_LENGTH} variables=${variables}
      onChange=${(value) => onConfig({ value })} error=${botFieldProblemText(problems, 'value')} />
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function GotoForm({ node, definition, problems, lookups, onConfig, onGoToNode }) {
  const config = node.config ?? {};
  const choices = gotoTargetChoices(definition, node.id);
  const extra = problems.find((problem) => problem.code === 'GOTO_TARGET_MISSING' || problem.code === 'GOTO_SELF');
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-goto-form">
    <${BotSelectField} label=${t('bots.forms.goto.target')} value=${config.targetNodeId} placeholder=${t('bots.forms.goto.choose')}
      options=${choices.map((choice, position) => ({ value: choice.id, label: `${position + 1}. ${describeBotNode(choice, lookups).title}` }))}
      onChange=${(targetNodeId) => onConfig({ targetNodeId })} error=${botFieldProblemText(problems, 'targetNodeId') ?? (extra ? botProblemText(extra) : null)} />
    ${config.targetNodeId && !extra
      ? html`<${Button} title=${t('bots.forms.goto.show')} icon="IconArrowForwardUp" variant="secondary" size="small" onClick=${() => onGoToNode?.(config.targetNodeId)} />`
      : null}
    <${FlowNotice}>${t('bots.forms.goto.notice')}<//>
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function SubbotForm({ node, botId, problems, variables, lookupData, onConfig }) {
  const config = node.config ?? {};
  const passed = Array.isArray(config.passVariables) ? config.passVariables : [];
  const extra = problems.find((problem) => problem.code.startsWith('SUBBOT_'));
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-subbot-form">
    <${BotSelectField} label=${t('bots.forms.subbot.bot')} value=${config.botId} placeholder=${t('bots.forms.subbot.choose')}
      options=${(lookupData.bots ?? []).filter((bot) => bot.id !== botId).map((bot) => ({ value: bot.id, label: `${bot.name} (${t(`bots.status.${bot.status}`)})` }))}
      onChange=${(value) => onConfig({ botId: value })} error=${botFieldProblemText(problems, 'botId') ?? (extra ? botProblemText(extra) : null)} />
    ${variables.length > 0
      ? html`<${FlowField} label=${t('bots.forms.subbot.passVariables')}>
          <div class="bot-rule-checks" role="group" aria-label=${t('bots.forms.subbot.passVariables')}>
            ${variables.map((name) => html`<label key=${name} class="flow-checkbox-row">
              <${Checkbox} checked=${passed.includes(name)} ariaLabel=${`bot.${name}`}
                onChange=${(/** @type {boolean} */ checked) => onConfig({ passVariables: checked ? [...passed, name] : passed.filter((/** @type {string} */ item) => item !== name) })} />
              <code>${`bot.${name}`}</code>
            </label>`)}
          </div>
        <//>`
      : null}
    <${FlowNotice}>${t('bots.forms.subbot.notice')}<//>
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function EndForm({ node, variables, onConfig }) {
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-end-form">
    <${BotTextField} label=${t('bots.forms.end.text')} value=${node.config?.text} multiline variables=${variables} hint=${t('bots.forms.end.hint')}
      onChange=${(text) => onConfig({ text: text || null })} />
  </div>`;
}

